/**
 * Admin V5 — Unified Students Page
 *
 * Single source of truth for ALL students across:
 *   - Portal signups (users.role=student)
 *   - Enquiry leads (students_v4)
 *   - Booking-only students (classroom_bookings)
 *
 * Merges by email, shows source pill, supports filtering by source / assignment /
 * program, and lets the admin assign a tutor with a single click.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Search, UserPlus, X, Check, ArrowLeft, Users, Filter, ExternalLink } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const ax = axios.create({ baseURL: API, withCredentials: true });

const SOURCE_BADGE = {
  portal_signup: 'bg-emerald-100 text-emerald-800',
  admin_created: 'bg-blue-100 text-blue-800',
  enquiry: 'bg-amber-100 text-amber-800',
  booking: 'bg-purple-100 text-purple-800',
};
const SOURCE_LABEL = {
  portal_signup: 'Portal Signup',
  admin_created: 'Admin Created',
  enquiry: 'Enquiry Lead',
  booking: 'From Booking',
};

const KPI_DEFS = [
  { key: 'total_unique_students', label: 'Total Students', tone: 'text-gray-900' },
  { key: 'portal_accounts',       label: 'Portal Accounts', tone: 'text-emerald-700' },
  { key: 'active_portal_students',label: 'Active Portal',   tone: 'text-emerald-700' },
  { key: 'enquiry_leads',         label: 'Enquiry Leads',   tone: 'text-amber-700' },
  { key: 'booking_only_students', label: 'Booking Only',    tone: 'text-purple-700' },
  { key: 'unassigned',            label: 'Unassigned',      tone: 'text-red-700' },
];

const AssignTutorModal = ({ open, student, onClose, onConfirm }) => {
  const [tutors, setTutors] = useState([]);
  const [tutorEmail, setTutorEmail] = useState('');
  const [note, setNote] = useState('');
  const [notifyTutor, setNotifyTutor] = useState(true);
  const [notifyStudent, setNotifyStudent] = useState(true);
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState('');
  useEffect(() => {
    if (!open) return;
    setTutorEmail(student?.assigned_tutor_email || '');
    setNote(''); setNotifyTutor(true); setNotifyStudent(true); setSearch('');
    ax.get('/api/admin/v5/assignable-tutors')
      .then((r) => setTutors(r.data?.items || []))
      .catch(() => setTutors([]));
  }, [open, student]);
  if (!open) return null;
  const ql = search.trim().toLowerCase();
  const visibleTutors = !ql ? tutors : tutors.filter((t) =>
    (t.tutor_name || '').toLowerCase().includes(ql) ||
    (t.tutor_email || '').toLowerCase().includes(ql) ||
    (t.subjects || '').toLowerCase().includes(ql));
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="assign-tutor-modal">
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[85vh] flex flex-col p-6">
        <div className="text-sm font-bold text-gray-900 flex items-center gap-2"><UserPlus className="h-4 w-4 text-red-700" /> Assign tutor to {student?.name || student?.email}</div>
        <div className="text-[11px] text-gray-500 mt-1">Shows every approved + active tutor with subjects, portal status and availability flag.</div>
        <div className="mt-4 space-y-3">
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search tutor by name, email or subject..." className="w-full h-10 px-3 rounded-lg border border-gray-200 text-xs" data-testid="assign-tutor-search" />
          <div className="border border-gray-200 rounded-lg max-h-[300px] overflow-y-auto divide-y divide-gray-100" data-testid="assign-tutor-list">
            {visibleTutors.length === 0 ? (
              <div className="p-4 text-center text-xs text-gray-400 italic">No tutors match.</div>
            ) : visibleTutors.map((t) => {
              const selected = tutorEmail === t.tutor_email;
              return (
                <button key={t.tutor_email} type="button" onClick={() => setTutorEmail(t.tutor_email)}
                  className={`w-full text-left p-3 hover:bg-gray-50 transition ${selected ? 'bg-red-50' : ''}`}
                  data-testid={`assign-tutor-option-${t.tutor_email}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="text-sm font-bold text-gray-900">{t.tutor_name}</div>
                        {selected && <Check className="h-3.5 w-3.5 text-red-700" />}
                      </div>
                      <div className="text-[11px] text-gray-600">{t.tutor_email}</div>
                      {t.subjects && <div className="text-[11px] text-gray-700 mt-1"><strong>Subjects:</strong> {t.subjects}</div>}
                      {t.qualifications && <div className="text-[10px] text-gray-500 mt-0.5">{t.qualifications}{t.years_experience ? ` · ${t.years_experience} yrs` : ''}</div>}
                    </div>
                    <div className="flex flex-col items-end gap-1 shrink-0">
                      <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider ${t.portal_account_status === 'active' ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-200 text-gray-700'}`}>{t.portal_account_status === 'active' ? 'Active' : t.portal_account_status}</span>
                      {t.availability_completed ? (
                        <span className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-200">Available</span>
                      ) : (
                        <span className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-amber-50 text-amber-800 border border-amber-200">No availability</span>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note for the tutor (optional)" className="w-full h-10 px-3 rounded-lg border border-gray-200 text-xs" data-testid="assign-note" />
          <div className="flex items-center gap-4 text-[11px] text-gray-700">
            <label className="flex items-center gap-1.5 cursor-pointer"><input type="checkbox" checked={notifyTutor} onChange={(e) => setNotifyTutor(e.target.checked)} /> Notify tutor</label>
            <label className="flex items-center gap-1.5 cursor-pointer"><input type="checkbox" checked={notifyStudent} onChange={(e) => setNotifyStudent(e.target.checked)} /> Notify student</label>
          </div>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="h-9 px-4 rounded-full bg-white border border-gray-200 text-xs font-bold uppercase tracking-wider" data-testid="assign-cancel">Cancel</button>
          <button disabled={busy || !tutorEmail} onClick={async () => {
            setBusy(true);
            try { await onConfirm({ tutor_email: tutorEmail, note, notify_tutor: notifyTutor, notify_student: notifyStudent }); }
            finally { setBusy(false); }
          }} className="h-9 px-5 rounded-full bg-red-700 hover:bg-red-800 text-white text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 disabled:opacity-40" data-testid="assign-confirm">
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />} Assign
          </button>
        </div>
      </div>
    </div>
  );
};

const AdminV5StudentsPage = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [planSummary, setPlanSummary] = useState({}); // keyed by email
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');
  const [source, setSource] = useState('');
  const [assigned, setAssigned] = useState('');
  const [assignOpen, setAssignOpen] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set('q', q.trim());
      if (source) params.set('source', source);
      if (assigned) params.set('assigned', assigned);
      params.set('limit', '500');
      const [list, kpi, plans] = await Promise.all([
        ax.get(`/api/admin/v5/students?${params.toString()}`),
        ax.get('/api/admin/v5/students/kpis'),
        ax.get('/api/admin/students-with-plans').catch(() => ({ data: { items: [] } })),
      ]);
      setItems(list.data?.items || []);
      setKpis(kpi.data || null);
      const map = {};
      (plans.data?.items || []).forEach(p => { map[p.email] = p; });
      setPlanSummary(map);
    } catch (e) {
      const code = e.response?.status;
      if (code === 401 || code === 403) navigate('/portal/login');
      else toast.error(e.response?.data?.detail || 'Could not load students.');
    } finally { setLoading(false); }
  }, [q, source, assigned, navigate]);

  useEffect(() => { load(); }, [load]);

  const onAssign = async (payload) => {
    try {
      const r = await ax.post(`/api/admin/v5/students/${encodeURIComponent(assignOpen.email)}/assign-tutor`, payload);
      toast.success(`Assigned ${r.data.tutor_name || r.data.tutor_email}`);
      setAssignOpen(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Assign failed'); }
  };

  const onUnassign = async (st) => {
    if (!window.confirm(`Unassign ${st.assigned_tutor_name || st.assigned_tutor_email} from ${st.email}?`)) return;
    try {
      await ax.delete(`/api/admin/v5/students/${encodeURIComponent(st.email)}/assign-tutor`);
      toast.success('Unassigned');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Unassign failed'); }
  };

  const visible = useMemo(() => items, [items]);

  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="admin-v5-students-page">
      <div className="bg-white border-b shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center gap-3 flex-wrap">
          <button onClick={() => navigate('/admin/dashboard')} className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900" data-testid="back-to-dashboard"><ArrowLeft className="h-4 w-4" /> Dashboard</button>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg sm:text-xl font-bold text-gray-900 flex items-center gap-2"><Users className="h-5 w-5 text-red-700" /> All Students <span className="text-[10px] uppercase tracking-wider bg-red-700 text-white px-2 py-0.5 rounded-full">V5 Unified</span></h1>
            <p className="text-[11px] sm:text-xs text-gray-500">Portal signups, enquiry leads & booking-only students — merged by email, no duplicates.</p>
          </div>
          <button onClick={load} disabled={loading} className="h-9 px-3 rounded-full border border-gray-200 text-[10px] font-bold uppercase tracking-wider flex items-center gap-1.5 disabled:opacity-40" data-testid="refresh">
            {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : null} Refresh
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-5">
        {/* KPIs */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="students-kpi-strip">
          {KPI_DEFS.map((d) => (
            <div key={d.key} className="bg-white rounded-xl border border-gray-100 p-3" data-testid={`kpi-${d.key}`}>
              <div className="text-[9px] uppercase tracking-wider text-gray-500 font-bold">{d.label}</div>
              <div className={`text-xl font-bold mt-0.5 ${d.tone}`}>{kpis ? (kpis[d.key] ?? 0) : '—'}</div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="bg-white border border-gray-200 rounded-2xl p-3 flex items-center gap-2 flex-wrap" data-testid="students-filters">
          <div className="relative flex-1 min-w-[180px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, email, phone…" className="w-full h-10 pl-9 pr-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-200" data-testid="students-search" />
          </div>
          <select value={source} onChange={(e) => setSource(e.target.value)} className="h-10 px-3 rounded-lg border border-gray-200 bg-white text-[12px]" data-testid="students-source-filter">
            <option value="">All sources</option>
            <option value="portal_signup">Portal Signup</option>
            <option value="enquiry">Enquiry Lead</option>
            <option value="booking">From Booking</option>
            <option value="admin_created">Admin Created</option>
          </select>
          <select value={assigned} onChange={(e) => setAssigned(e.target.value)} className="h-10 px-3 rounded-lg border border-gray-200 bg-white text-[12px]" data-testid="students-assigned-filter">
            <option value="">Any assignment</option>
            <option value="no">Unassigned</option>
            <option value="yes">Assigned</option>
          </select>
          {(q || source || assigned) && (
            <button onClick={() => { setQ(''); setSource(''); setAssigned(''); }} className="h-10 px-3 rounded-lg border border-gray-200 text-[10px] uppercase tracking-wider font-bold flex items-center gap-1"><X className="h-3 w-3" /> Clear</button>
          )}
          <div className="text-[11px] text-gray-500 ml-auto" data-testid="students-count">{visible.length} shown</div>
        </div>

        {/* List */}
        <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
          {loading && (
            <div className="flex items-center justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-red-700" /></div>
          )}
          {!loading && visible.length === 0 && (
            <div className="text-center py-12 text-sm text-gray-400 italic" data-testid="students-empty">
              <Filter className="h-5 w-5 mx-auto mb-2 text-gray-300" /> No students match these filters.
            </div>
          )}
          {!loading && visible.map((s) => (
            <div key={s.email} className="border-b border-gray-100 last:border-b-0 p-4 flex items-start justify-between gap-3 flex-wrap hover:bg-gray-50" data-testid={`student-row-${s.email}`}>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="text-sm font-bold text-gray-900">{s.name}</div>
                  <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${SOURCE_BADGE[s.source] || 'bg-gray-100 text-gray-700'}`} data-testid={`source-${s.email}`}>
                    {SOURCE_LABEL[s.source] || s.source}
                  </span>
                  {s.account_status === 'inactive' && <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-red-100 text-red-700">Inactive</span>}
                  {!s.assigned_tutor_email && <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-red-50 text-red-700 border border-red-200">Unassigned</span>}
                </div>
                <div className="text-[11px] text-gray-600 mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                  <span><strong>Email:</strong> {s.email}</span>
                  {s.phone && <span><strong>Phone:</strong> {s.phone}</span>}
                  {s.parent_email && <span><strong>Parent:</strong> {s.parent_email}</span>}
                  {s.program && <span><strong>Program:</strong> {s.program}</span>}
                  {s.subject_interest && <span><strong>Interest:</strong> {s.subject_interest}</span>}
                  {s.booking_count != null && <span><strong>Bookings:</strong> {s.booking_count}</span>}
                </div>
                {s.assigned_tutor_email && (
                  <div className="text-[11px] text-emerald-700 mt-1.5"><strong>Tutor:</strong> {s.assigned_tutor_name || s.assigned_tutor_email}</div>
                )}
                {s.lead_message && <div className="text-[11px] text-gray-500 mt-1 italic">"{s.lead_message}"</div>}
                {/* Phase 5F — Learning Plan summary */}
                {planSummary[s.email] && (planSummary[s.email].subject_count > 0) && (
                  <div className="mt-2 bg-gradient-to-br from-red-50 to-amber-50 border border-red-200 rounded-lg p-2.5" data-testid={`plan-summary-${s.email}`}>
                    <div className="flex items-center gap-1.5 flex-wrap text-[10px]">
                      <span className="font-bold text-red-900 uppercase tracking-wider">Learning Plan</span>
                      <span className={`px-1.5 py-0.5 rounded font-bold uppercase tracking-wider ${planSummary[s.email].payment_status === 'paid' ? 'bg-emerald-100 text-emerald-800' : planSummary[s.email].payment_status === 'partial' ? 'bg-amber-100 text-amber-800' : 'bg-rose-100 text-rose-800'}`}>{planSummary[s.email].payment_status}</span>
                      <span className="text-gray-700">· {planSummary[s.email].subject_count} subjects</span>
                      <span className="text-gray-700">· {planSummary[s.email].tutor_count} tutors</span>
                      <span className="text-gray-700">· AUD {(planSummary[s.email].total_amount_aud_cents / 100).toFixed(0)}</span>
                      <span className="text-gray-700">· {planSummary[s.email].sessions_completed}/{planSummary[s.email].sessions_total} done</span>
                      <span className="text-gray-700">· {planSummary[s.email].total_bookings} bookings</span>
                    </div>
                    {planSummary[s.email].subjects?.length > 0 && (
                      <div className="mt-1.5 flex flex-wrap gap-1">
                        {planSummary[s.email].subjects.slice(0, 6).map((sub, i) => (
                          <span key={`${s.email}-${i}-${sub}`} className="bg-white border border-red-100 rounded px-1.5 py-0.5 text-[9px] text-gray-700">{sub}</span>
                        ))}
                        {planSummary[s.email].subjects.length > 6 && (
                          <span className="text-[9px] text-gray-500">+{planSummary[s.email].subjects.length - 6}</span>
                        )}
                      </div>
                    )}
                    {planSummary[s.email].schedule?.length > 0 && (
                      <div className="mt-1.5 text-[9px] text-gray-600">{planSummary[s.email].schedule.slice(0, 3).join(' · ')}{planSummary[s.email].schedule.length > 3 && ` · +${planSummary[s.email].schedule.length - 3} more`}</div>
                    )}
                  </div>
                )}
                <div className="text-[10px] text-gray-400 mt-1">Last activity: {s.latest_activity_at ? new Date(s.latest_activity_at).toLocaleString() : '—'}</div>
              </div>
              <div className="flex items-center gap-1.5 flex-wrap">
                <button onClick={() => setAssignOpen(s)} className="h-8 px-3 rounded-full bg-red-700 hover:bg-red-800 text-white text-[10px] font-bold uppercase tracking-wider flex items-center gap-1" data-testid={`assign-${s.email}`}>
                  <UserPlus className="h-3 w-3" /> {s.assigned_tutor_email ? 'Reassign' : 'Assign tutor'}
                </button>
                {s.assigned_tutor_email && (
                  <button onClick={() => onUnassign(s)} className="h-8 px-3 rounded-full border border-gray-200 text-[10px] font-bold uppercase tracking-wider flex items-center gap-1" data-testid={`unassign-${s.email}`}>
                    <X className="h-3 w-3" /> Unassign
                  </button>
                )}
                {s.has_booking_record && (
                  <button onClick={() => navigate('/admin/booking-requests')} className="h-8 px-3 rounded-full border border-gray-200 text-[10px] font-bold uppercase tracking-wider flex items-center gap-1">
                    <ExternalLink className="h-3 w-3" /> Bookings
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <AssignTutorModal open={!!assignOpen} student={assignOpen} onClose={() => setAssignOpen(null)} onConfirm={onAssign} />
    </div>
  );
};

export default AdminV5StudentsPage;
